import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  CircularProgress
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ApartmentIcon from '@mui/icons-material/Apartment';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import DoorBackIcon from '@mui/icons-material/DoorBack';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import axios from 'axios';

const ConfigCards = ({ onAnnotateImage, onServiceSelected, annotatedZones = {} }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedZone, setSelectedZone] = useState(null);
  const [editingZone, setEditingZone] = useState(null);
  const [editingZoneValue, setEditingZoneValue] = useState('');
  const [customZones, setCustomZones] = useState({});
  const [additionalZones, setAdditionalZones] = useState({});
  const [newZoneName, setNewZoneName] = useState('');
  const [isAddingZone, setIsAddingZone] = useState(false);
  const [processedCardConfig, setProcessedCardConfig] = useState([]);
  const [zoneType, setZoneType] = useState('Zone');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const getIconForService = (serviceName) => {
    const iconStyle = {
      background: 'linear-gradient(135deg, #44BE54, #2AA93E)',
      color: 'white',
      borderRadius: '1.5rem',
      padding: '10px',
      boxShadow: '0px 6px 20px rgba(68, 190, 84, 0.35)',
      transition: 'all 0.3s ease-in-out',
      '&:hover': {
        background: 'linear-gradient(135deg, #3AA74A, #259435)',
        transform: 'translateY(-2px)',
        boxShadow: '0px 8px 25px rgba(68, 190, 84, 0.55)'
      }
    };

    switch (serviceName) {
      case 'Entry Exit':
        return <DoorBackIcon fontSize="large" sx={iconStyle} />;
      case 'Departments':
        return <ApartmentIcon fontSize="large" sx={iconStyle} />;
      case 'POS':
        return <PointOfSaleIcon fontSize="large" sx={iconStyle} />;
      case 'Pathway':
        return <AltRouteIcon fontSize="large" sx={iconStyle} />;
      default:
        return <DoorBackIcon fontSize="large" sx={iconStyle} />;
    }
  };

  const fetchServices = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('http://localhost:7072/api/sentinel/services');

      const data = response.data;

      if (data && data.services && Array.isArray(data.services)) {
        const transformedConfig = data.services.map((service) => ({
          _id: service._id,
          name: service.service_name,
          description: service.descriptions || '',
          fixedZones: service.fixed_zone,
          zoneList: Array.isArray(service.zone_list)
            ? service.zone_list.map((zone) => (typeof zone === 'string' ? zone : zone.toString()))
            : []
        }));

        processServicesData(transformedConfig);
      } else {
        throw new Error('Invalid data format received from API');
      }
    } catch (err) {
      console.error('Error fetching services:', err);
      setError(`Failed to load services: ${err.message}`);

      // const sampleServices = [
      //   {
      //     _id: 'entry_exit_id',
      //     name: 'Entry Exit',
      //     description: 'Configure Entry Exit settings.',
      //     fixedZones: true,
      //     zoneList: ['Main Entrance', 'Side Entrance', 'Exit Door']
      //   },
      //   {
      //     _id: 'departments_id',
      //     name: 'Departments',
      //     description: 'Configure different department zones.',
      //     fixedZones: false,
      //     zoneList: ['Department 1', 'Department 2']
      //   },
      //   {
      //     _id: 'pos_id',
      //     name: 'POS',
      //     description: 'Configure Point of Sale locations.',
      //     fixedZones: true,
      //     zoneList: ['Checkout 1', 'Checkout 2', 'Service Desk']
      //   },
      //   {
      //     _id: 'pathway_id',
      //     name: 'Pathway',
      //     description: 'Configure pathway routes in the store.',
      //     fixedZones: false,
      //     zoneList: ['Pathway 1', 'Pathway 2']
      //   }
      // ];

      // processServicesData(sampleServices);
    } finally {
      setLoading(false);
    }
  };

  // Process the services data and set up the card configuration
  const processServicesData = (servicesData) => {
    const updatedConfig = servicesData.map((card) => {
      let zones = [];

      if (card.fixedZones && card.zoneList) {
        zones = [...card.zoneList];
      }

      if (!card.fixedZones && additionalZones[card.name]) {
        zones = [...zones, ...additionalZones[card.name]];
      }

      return { ...card, zones };
    });

    setProcessedCardConfig(updatedConfig);

    if (selectedCard) {
      const updatedSelectedCard = updatedConfig.find((card) => card.name === selectedCard.name);
      if (updatedSelectedCard) {
        setSelectedCard(updatedSelectedCard);
      }
    }
  };

  // Fetch services on component mount
  useEffect(() => {
    fetchServices();
  }, []);

  // Update processed card config when additionalZones changes
  useEffect(() => {
    if (processedCardConfig.length > 0) {
      const updatedConfig = processedCardConfig.map((card) => {
        let zones = [];

        if (card.fixedZones && card.zoneList) {
          zones = [...card.zoneList];
        }

        if (!card.fixedZones && additionalZones[card.name]) {
          zones = [...zones, ...additionalZones[card.name]];
        }

        return { ...card, zones };
      });

      setProcessedCardConfig(updatedConfig);

      if (selectedCard) {
        const updatedSelectedCard = updatedConfig.find((card) => card.name === selectedCard.name);
        if (updatedSelectedCard) {
          setSelectedCard(updatedSelectedCard);
        }
      }
    }
  }, [additionalZones, selectedCard?.name]);

  const handleCardClick = (card) => {
    setSelectedCard(card);
    setSelectedZone(null);
    setEditingZone(null);
    setIsAddingZone(false);
    setNewZoneName('');

    if (onServiceSelected) {
      onServiceSelected(card.name, card._id); // Pass both name and ID
    }
  };

  const handleZoneSelect = (zone) => {
    if (isZoneAnnotated(selectedCard.name, zone)) return;

    setSelectedZone(zone);
    setEditingZone(null);
    setIsAddingZone(false);
  };

  const handleEditZone = (zone) => {
    if (isZoneAnnotated(selectedCard.name, zone)) return;

    // Don't allow editing for fixed zones
    if (selectedCard.fixedZones) return;

    setEditingZone(zone);
    setEditingZoneValue(customZones[selectedCard.name]?.[zone] || zone);
    setIsAddingZone(false);
  };

  const handleSaveZoneName = () => {
    if (editingZoneValue.trim() === '') return;

    setCustomZones((prev) => ({
      ...prev,
      [selectedCard.name]: {
        ...(prev[selectedCard.name] || {}),
        [editingZone]: editingZoneValue
      }
    }));

    if (selectedZone === editingZone) {
      setSelectedZone(editingZoneValue);
    }

    setEditingZone(null);
  };

  const getNextZoneNumber = (cardName) => {
    if (cardName !== 'Departments' && cardName !== 'Pathway') return 1;

    const card = processedCardConfig.find((c) => c.name === cardName);
    const baseZoneCount = card && card.zoneList ? card.zoneList.length : 0;
    const additionalZoneCount = (additionalZones[cardName] || []).length;

    return baseZoneCount + additionalZoneCount + 1;
  };

  const handleAddZoneClick = () => {
    setIsAddingZone(true);
    setEditingZone(null);

    if (selectedCard.name === 'Departments') {
      const nextNumber = getNextZoneNumber('Departments');
      setNewZoneName(`Department ${nextNumber}`);
    } else if (selectedCard.name === 'Pathway') {
      const nextNumber = getNextZoneNumber('Pathway');
      setNewZoneName(`Pathway ${nextNumber}`);
    } else {
      setNewZoneName('');
    }
  };

  const handleSaveNewZone = () => {
    if (newZoneName.trim() === '') return;

    const cardName = selectedCard.name;
    const newZoneId = `${cardName}_${Date.now().toString().slice(-4)}`;

    setAdditionalZones((prev) => ({
      ...prev,
      [cardName]: [...(prev[cardName] || []), newZoneId]
    }));

    setCustomZones((prev) => ({
      ...prev,
      [cardName]: {
        ...(prev[cardName] || {}),
        [newZoneId]: newZoneName
      }
    }));

    setNewZoneName('');
    setIsAddingZone(false);
  };

  const getZoneDisplayName = (zone) => {
    return customZones[selectedCard?.name]?.[zone] || zone;
  };

  const isZoneAnnotated = (cardName, zone) => {
    const displayName = customZones[cardName]?.[zone] || zone;
    const formattedZoneName = `${cardName}_${displayName.replace(/\s+/g, '_')}`;

    return (
      annotatedZones[cardName]?.some((name) => {
        const baseName = name.includes('_') ? name.split('_').slice(1).join('_') : name;
        const zoneName = displayName.replace(/\s+/g, '_');

        return name === formattedZoneName || baseName === zoneName;
      }) || false
    );
  };

  const handleAnnotateClick = () => {
    if (selectedZone && onAnnotateImage) {
      const zoneDisplayName = getZoneDisplayName(selectedZone);

      onAnnotateImage({
        card: selectedCard.name,
        zone: zoneDisplayName,
        type: selectedCard.name === 'Departments' ? zoneType : null
      });

      setSelectedZone(null);
    }
  };

  const handleBack = () => {
    setSelectedCard(null);
    setSelectedZone(null);
    if (onServiceSelected) {
      onServiceSelected('Services');
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress sx={{ color: '#44BE54' }} />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading services...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 4,
          border: '1px solid #ff6b6b',
          borderRadius: 2,
          bgcolor: 'rgba(255, 107, 107, 0.05)',
          textAlign: 'center'
        }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          Error Loading Services
        </Typography>
        <Typography variant="body1">{error}</Typography>
        <Button variant="outlined" color="primary" onClick={fetchServices} sx={{ mt: 3 }}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box className="config-cards-container" sx={{ width: '100%' }}>
      {!selectedCard ? (
        <>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 3,
              color: '#333',
              position: 'relative',
              display: 'inline-block',
              pb: 1,
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                width: '60%',
                height: '3px',
                background: 'linear-gradient(90deg, #44BE54, transparent)',
                borderRadius: '4px'
              }
            }}
          >
            Choose a Service
          </Typography>

          <Box
            display="grid"
            gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(2, 1fr)' }}
            gap={4}
            sx={{ width: '100%' }}
          >
            {processedCardConfig.map((card, index) => (
              <Card
                key={index}
                onClick={() => handleCardClick(card)}
                sx={{
                  minHeight: 160,
                  boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '1px solid rgba(0,0,0,0.04)',
                  '&:hover': {
                    transform: 'translateY(-10px)',
                    boxShadow: '0 16px 40px rgba(68, 190, 84, 0.12)'
                  },
                  overflow: 'hidden',
                  background: 'linear-gradient(145deg, #ffffff, #f8f9fa)',
                  position: 'relative',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '4px',
                    background: 'linear-gradient(90deg, #44BE54, #2AA93E)',
                    opacity: 0.8
                  }
                }}
              >
                <CardContent sx={{ p: 3, height: '100%' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ mr: 2 }}>{getIconForService(card.name)}</Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, letterSpacing: '-0.5px' }}>
                        {card.name}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{
                        fontSize: '0.95rem',
                        flexGrow: 1,
                        opacity: 0.85,
                        lineHeight: 1.5
                      }}
                    >
                      {card.description}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </>
      ) : (
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mb: 4,
              pb: 2,
              borderBottom: '1px solid rgba(0,0,0,0.08)'
            }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                mr: 2,
                bgcolor: 'rgba(0,0,0,0.04)',
                '&:hover': {
                  bgcolor: 'rgba(68, 190, 84, 0.1)',
                  transform: 'translateX(-3px)'
                },
                transition: 'all 0.2s ease'
              }}
            >
              <ArrowBackIcon />
            </IconButton>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ mr: 2 }}>{getIconForService(selectedCard.name)}</Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  backgroundImage: 'linear-gradient(45deg, #333, #555)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {selectedCard.name}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 3
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 500,
                  position: 'relative',
                  display: 'inline-block',
                  pb: 1,
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    width: '40%',
                    height: '2px',
                    background: 'linear-gradient(90deg, #44BE54, transparent)',
                    borderRadius: '4px'
                  }
                }}
              >
                Available Zones
              </Typography>

              {!selectedCard.fixedZones && (
                <Button
                  onClick={handleAddZoneClick}
                  startIcon={<AddCircleIcon />}
                  variant="contained"
                  sx={{
                    bgcolor: '#44BE54',
                    '&:hover': {
                      bgcolor: '#3AAD4A',
                      transform: 'translateY(-2px)',
                      boxShadow: '0px 6px 16px rgba(68, 190, 84, 0.35)'
                    },
                    borderRadius: 2,
                    px: 3,
                    py: 1.2,
                    boxShadow: '0px 4px 14px rgba(68, 190, 84, 0.25)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  Add Zone
                </Button>
              )}
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, 1fr)',
                  md: 'repeat(3, 1fr)'
                },
                gap: 3
              }}
            >
              {isAddingZone && !selectedCard.fixedZones && (
                <Card
                  sx={{
                    bgcolor: 'rgba(68, 190, 84, 0.05)',
                    border: '1px dashed #44BE54',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0px 4px 16px rgba(68, 190, 84, 0.1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
                    <TextField
                      fullWidth
                      autoFocus
                      placeholder="Enter new zone name"
                      value={newZoneName}
                      onChange={(e) => setNewZoneName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSaveNewZone()}
                      variant="outlined"
                      size="small"
                      sx={{
                        mr: 1,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: 'rgba(68, 190, 84, 0.4)'
                          },
                          '&:hover fieldset': {
                            borderColor: 'rgba(68, 190, 84, 0.7)'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#44BE54'
                          }
                        }
                      }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleSaveNewZone}
                      sx={{
                        color: '#44BE54',
                        '&:hover': {
                          bgcolor: 'rgba(68, 190, 84, 0.15)',
                          transform: 'scale(1.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <CheckIcon />
                    </IconButton>
                  </CardContent>
                </Card>
              )}

              {selectedCard?.zones?.length > 0 ? (
                selectedCard.zones.map((zone, index) => {
                  const isAnnotated = isZoneAnnotated(selectedCard.name, zone);
                  return (
                    <Card
                      key={index}
                      onClick={() => handleZoneSelect(zone)}
                      sx={{
                        borderRadius: 2,
                        cursor: isAnnotated ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s ease',
                        bgcolor: isAnnotated ? 'rgba(0, 0, 0, 0.04)' : selectedZone === zone ? 'rgba(68, 190, 84, 0.08)' : 'white',
                        border:
                          selectedZone === zone
                            ? '2px solid #44BE54'
                            : isAnnotated
                            ? '1px solid rgba(0, 0, 0, 0.12)'
                            : '1px solid rgba(0, 0, 0, 0.08)',
                        opacity: isAnnotated ? 0.7 : 1,
                        boxShadow: selectedZone === zone ? '0 4px 16px rgba(68, 190, 84, 0.25)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
                        '&:hover': {
                          boxShadow: isAnnotated ? 'none' : '0 6px 18px rgba(0, 0, 0, 0.15)',
                          transform: isAnnotated ? 'none' : 'translateY(-5px)'
                        },
                        position: 'relative',
                        overflow: 'hidden',
                        '&:before':
                          selectedZone === zone
                            ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '3px',
                                background: 'linear-gradient(90deg, #44BE54, #2AA93E)'
                              }
                            : {}
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        {editingZone === zone ? (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <TextField
                              fullWidth
                              autoFocus
                              value={editingZoneValue}
                              onChange={(e) => setEditingZoneValue(e.target.value)}
                              onKeyPress={(e) => e.key === 'Enter' && handleSaveZoneName()}
                              onClick={(e) => e.stopPropagation()}
                              variant="outlined"
                              size="small"
                              sx={{
                                mr: 1,
                                '& .MuiOutlinedInput-root': {
                                  '& fieldset': {
                                    borderColor: 'rgba(68, 190, 84, 0.4)'
                                  },
                                  '&:hover fieldset': {
                                    borderColor: 'rgba(68, 190, 84, 0.7)'
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#44BE54'
                                  }
                                }
                              }}
                            />
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSaveZoneName();
                              }}
                              sx={{
                                color: '#44BE54',
                                '&:hover': {
                                  bgcolor: 'rgba(68, 190, 84, 0.15)',
                                  transform: 'scale(1.1)'
                                },
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <CheckIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                              <Typography
                                variant="subtitle1"
                                sx={{
                                  fontWeight: 500,
                                  color: isAnnotated ? 'text.disabled' : 'text.primary',
                                  textDecoration: isAnnotated ? 'line-through' : 'none',
                                  letterSpacing: '0.2px'
                                }}
                              >
                                {getZoneDisplayName(zone)}
                              </Typography>
                              {isAnnotated && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    ml: 1.5,
                                    bgcolor: 'rgba(0, 0, 0, 0.06)',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    color: 'text.secondary',
                                    fontWeight: 500
                                  }}
                                >
                                  Annotated
                                </Typography>
                              )}
                            </Box>

                            {!isAnnotated && !selectedCard.fixedZones && editingZone !== zone && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditZone(zone);
                                }}
                                sx={{
                                  color: 'action.active',
                                  '&:hover': {
                                    color: '#44BE54',
                                    transform: 'scale(1.1)'
                                  },
                                  transition: 'all 0.2s ease'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              ) : (
                <Typography variant="body1" color="text.secondary" sx={{ gridColumn: '1 / -1', textAlign: 'center', py: 3 }}>
                  No zones available for this service.
                </Typography>
              )}
            </Box>
          </Box>

          {selectedZone && (
            <Box
              sx={{
                mt: 4,
                p: 4,
                background: 'linear-gradient(145deg, rgba(68, 190, 84, 0.04), rgba(68, 190, 84, 0.08))',
                borderRadius: 3,
                border: '1px solid rgba(68, 190, 84, 0.12)',
                boxShadow: '0 6px 20px rgba(68, 190, 84, 0.1)',
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '4px',
                  background: 'linear-gradient(90deg, #44BE54, #2AA93E)',
                  opacity: 0.7
                }
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  mb: 3,
                  fontWeight: 500,
                  color: '#333',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                Selected Zone:{' '}
                <Box
                  component="span"
                  sx={{
                    fontWeight: 600,
                    color: '#2AA93E',
                    backgroundImage: 'linear-gradient(45deg, #2AA93E, #44BE54)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                  }}
                >
                  {getZoneDisplayName(selectedZone)}
                </Box>
              </Typography>

              {selectedCard.name === 'Departments' && (
                <FormControl
                  fullWidth
                  sx={{
                    mb: 4,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: 'rgba(68, 190, 84, 0.3)',
                        borderRadius: 2
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(68, 190, 84, 0.5)'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#44BE54'
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#44BE54'
                    }
                  }}
                >
                  <InputLabel id="zone-type-label">Zone Type</InputLabel>
                  <Select
                    labelId="zone-type-label"
                    id="zone-type-select"
                    value={zoneType}
                    label="Zone Type"
                    onChange={(e) => setZoneType(e.target.value)}
                  >
                    <MenuItem value="Zone">Zone</MenuItem>
                    <MenuItem value="Shelf">Shelf</MenuItem>
                  </Select>
                </FormControl>
              )}

              <Button
                variant="contained"
                onClick={handleAnnotateClick}
                fullWidth
                sx={{
                  bgcolor: '#44BE54',
                  '&:hover': {
                    bgcolor: '#3AAD4A',
                    transform: 'translateY(-2px)',
                    boxShadow: '0px 6px 16px rgba(68, 190, 84, 0.35)'
                  },
                  borderRadius: 2,
                  py: 1.5,
                  boxShadow: '0px 4px 14px rgba(68, 190, 84, 0.25)',
                  transition: 'all 0.3s ease',
                  fontSize: '1rem',
                  fontWeight: 500,
                  textTransform: 'none'
                }}
              >
                Annotate {getZoneDisplayName(selectedZone)}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ConfigCards;
